/**
 * Generación de PDF en el navegador (on-demand, sin guardar en servidor).
 * Usamos la API del navegador para imprimir/guardar como PDF o un enfoque canvas.
 * Decisión: generación en cliente con jsPDF o similar para no depender de Cloud Functions
 * y cumplir "NO guardar PDF en servidor". Alternativa sería Cloud Function que genera y
 * devuelve stream; por simplicidad y privacidad elegimos cliente.
 */

import { getCommercialName } from '@/config/batteries';
import { formatDateMexicoCity, formatDateTimeMexicoCity } from '@/lib/dateUtils';
import { computeAffinityInterpretation, getMatchLabel } from '@/lib/affinityInterpretation';
import {
  ML8_DERIVED_NOTE_EXPORT as ml8DerivedNote,
  APO6_NOTE as apo6Note,
  getRA10RhSummary,
  computePCL10DerivedConfianza,
  computeCriterioOperativoNormasDerived,
  computeEstiloDecisionesDerived,
  CONFIABILIDAD_ETICA_DERIVED_NOTE,
  CRITERIO_OPERATIVO_DERIVED_NOTE,
  ESTILO_DECISIONES_DERIVED_NOTE,
  RA_10_DERIVED_NOTE,
} from '@/lib/instrumentScoring';
import type { CultureScore } from '@/lib/cultureEngine';

export interface ReportPdfOptions {
  attemptId: string;
  candidateName: string;
  processTitle: string;
  companyName: string;
  logoUrl?: string;
  primaryColor?: string;
  /** Fecha de completado de la evaluación (ISO). Si se pasa, se muestra en zona horaria Ciudad de México. */
  computedAt?: string;
  scores: {
    testId: string;
    rawScore?: number;
    scaleScore?: number;
    band?: string;
    bandLabel?: string;
    rhSummary?: string;
    commercialName?: string;
    scorePercent?: number;
  }[];
  moduleAggregates: Record<string, unknown>;
  /** Perfil del puesto (benchmark) para interpretación de afinidad. */
  jobProfile?: Record<string, { testId: string; minScore?: number; weight?: number; dimensions?: Record<string, { expectedLevel: string; weight: number }> }>;
  roleType?: string | null;
  culturalFit?: {
    percent: number;
    candidateScores: CultureScore;
    companyScores: CultureScore;
  } | null;
}

/**
 * Genera y descarga el PDF en el cliente.
 * Incluye puntuaciones, rango, resumen RH y (si aplica) estilo de decisión PED.
 */
export async function generateReportPdf(options: ReportPdfOptions): Promise<void> {
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Permite ventanas emergentes para descargar el PDF.');
  }
  const isPercentInstrument = (testId: string) =>
    testId === 'JK_OFFICE_ADMIN' || testId === 'JK_SALES_RETAIL' || testId === 'APO_6';
  const rows = options.scores
    .filter((s) => s.testId !== 'ML_8' && s.testId !== 'MINI_IPIP_20')
    .map(
      (s) => {
        const displayName = s.testId === 'RA_10' ? getCommercialName('estilo_trabajo_organizacion') : (s.commercialName ?? getCommercialName(s.testId));
        const val = s.scaleScore ?? s.rawScore;
        const displayScore =
          val != null && isPercentInstrument(s.testId) ? `Porcentaje: ${val}%` : (val ?? '-');
        return `<tr><td>${escapeHtml(displayName)}</td><td>${displayScore}</td><td>${escapeHtml(s.bandLabel ?? s.band ?? '-')}</td><td>${escapeHtml((s.rhSummary ?? '').slice(0, 200))}${(s.rhSummary ?? '').length > 200 ? '…' : ''}</td></tr>`;
      }
    )
    .join('');

  const reportDate = options.computedAt
    ? formatDateMexicoCity(options.computedAt)
    : formatDateMexicoCity(new Date());
  const generatedAt = formatDateTimeMexicoCity(new Date());

  const rfbAgg = options.moduleAggregates.RFB_SJ_8 as Record<string, unknown> | undefined;
  const decisionStyle = rfbAgg?.decisionStyle as Record<string, string> | undefined;
  const pedSection =
    decisionStyle?.rhStyleSummary
      ? `
      <h2>Estilo de decisión (RFB-SJ)</h2>
      <p style="margin: 0.5em 0;">${escapeHtml(decisionStyle.rhStyleSummary ?? '')}</p>
      <hr/>`
      : '';

  const eel12Agg = options.moduleAggregates.EEL_12 as Record<string, unknown> | undefined;
  const eel12Section =
    eel12Agg
      ? `
      <h2>Autogestión Emocional en el Trabajo (EEL-12)</h2>
      <p style="margin: 0.5em 0;"><strong>Puntaje:</strong> ${eel12Agg.score50 ?? '—'} · <strong>Rango:</strong> ${escapeHtml(String(eel12Agg.adequacyLabel ?? eel12Agg.bandLabel ?? '—'))}</p>
      <p style="margin: 0.5em 0;">${escapeHtml(String(eel12Agg.interpretationText ?? ''))}</p>
      <p style="margin: 0.5em 0;"><strong>Calidad de respuesta:</strong> ${escapeHtml(String(eel12Agg.consistencyLabel ?? '—'))}</p>
      ${eel12Agg.consistencyNote ? `<p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(String(eel12Agg.consistencyNote))}</p>` : ''}
      <hr/>`
      : '';

  const noSeNote = (agg: Record<string, unknown> | undefined) =>
    agg && (agg.answeredNoSeCount as number) >= 5
      ? '<p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">Se observaron varias respuestas "No sé". Puede sugerir falta de familiaridad con el contenido o cautela al responder.</p>'
      : '';

  const jkOaAgg = options.moduleAggregates.JK_OFFICE_ADMIN as Record<string, unknown> | undefined;
  const jkOaSection =
    jkOaAgg
      ? `
      <h2>Conocimiento del puesto — Oficina administrativa</h2>
      <p style="margin: 0.5em 0;"><strong>Aciertos:</strong> ${jkOaAgg.correctCount ?? '—'}/10 · <strong>Porcentaje:</strong> ${jkOaAgg.scorePercent ?? '—'}%</p>
      <p style="margin: 0.5em 0;"><strong>Rango:</strong> ${escapeHtml(String(jkOaAgg.bandLabel ?? '—'))}</p>
      ${noSeNote(jkOaAgg)}
      <hr/>`
      : '';

  const jkSrAgg = options.moduleAggregates.JK_SALES_RETAIL as Record<string, unknown> | undefined;
  const jkSrSection =
    jkSrAgg
      ? `
      <h2>Conocimiento del puesto — Ventas / vendedor de piso</h2>
      <p style="margin: 0.5em 0;"><strong>Aciertos:</strong> ${jkSrAgg.correctCount ?? '—'}/10 · <strong>Porcentaje:</strong> ${jkSrAgg.scorePercent ?? '—'}%</p>
      <p style="margin: 0.5em 0;"><strong>Rango:</strong> ${escapeHtml(String(jkSrAgg.bandLabel ?? '—'))}</p>
      ${noSeNote(jkSrAgg)}
      <hr/>`
      : '';

  const ra10Agg = options.moduleAggregates.RA_10 as Record<string, unknown> | undefined;
  const ra10Derived = ra10Agg?.derived as Record<string, { derived?: boolean; percent?: number; adecuacion?: string; text?: string }> | undefined;
  let responsabilidadDerived = ra10Derived?.responsabilidadControl;
  if (!responsabilidadDerived && ra10Agg && ra10Agg.score50 != null) {
    responsabilidadDerived = {
      percent: Math.round((Number(ra10Agg.score50) / 50) * 100),
      adecuacion: String(ra10Agg.bandLabel ?? '—'),
      text: ra10Agg.bandKey ? getRA10RhSummary(ra10Agg.bandKey as string) : undefined,
    };
  }
  const ra10RhText = ra10Agg?.bandKey ? getRA10RhSummary(ra10Agg.bandKey as string) : '';
  const ra10Section =
    ra10Agg
      ? `
      <h2>Estilo de Trabajo y Organización Laboral</h2>
      <p style="margin: 0.5em 0;"><strong>Puntaje:</strong> ${ra10Agg.score50 ?? '—'} · <strong>Rango:</strong> ${escapeHtml(String(ra10Agg.bandLabel ?? '—'))}</p>
      ${ra10RhText ? `<p style="margin: 0.5em 0;">${escapeHtml(ra10RhText)}</p>` : ''}
      <hr/>`
      : '';
  const ra10DerivedSection =
    ra10Agg && responsabilidadDerived
      ? `
      <h2>Responsabilidad y Control en el Trabajo</h2>
      <p style="margin: 0.5em 0;"><strong>Porcentaje:</strong> ${responsabilidadDerived.percent ?? '—'}% · <strong>Adecuación:</strong> ${escapeHtml(String(responsabilidadDerived.adecuacion ?? '—'))}</p>
      ${responsabilidadDerived.text ? `<p style="margin: 0.5em 0;">${escapeHtml(responsabilidadDerived.text)}</p>` : ''}
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(RA_10_DERIVED_NOTE)}</p>
      <hr/>`
      : '';

  const apo6Agg = options.moduleAggregates.APO_6 as Record<string, unknown> | undefined;
  const apo6Section =
    apo6Agg
      ? `
      <h2>Atención y Precisión Operativa (APO-6)</h2>
      <p style="margin: 0.5em 0;"><strong>Porcentaje:</strong> ${apo6Agg.percent ?? '—'}%</p>
      <p style="margin: 0.5em 0;"><strong>Adecuación:</strong> ${escapeHtml(String(apo6Agg.adecuacionLabel ?? apo6Agg.adecuacion ?? '—'))}</p>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(apo6Note)}</p>
      <hr/>`
      : '';

  const miniIPIPAgg = options.moduleAggregates.MINI_IPIP_20 as Record<string, unknown> | undefined;
  const miniIPIPDims = miniIPIPAgg?.dimensions as Record<string, { percent: number; level: string; text: string; normUsed?: string; nNorm?: number }> | undefined;
  const firstMiniDim = miniIPIPDims ? Object.values(miniIPIPDims)[0] : undefined;
  const normLabel = firstMiniDim?.normUsed
    ? { company: 'Empresa', industry: 'Industria', country: 'País', global: 'Global', global_seed: 'Semilla', fallback: 'Fallback' }[firstMiniDim.normUsed] ?? firstMiniDim.normUsed
    : 'Fallback';
  const nNorm = firstMiniDim?.nNorm ?? 0;
  const MINI_IPIP_DIM_NAMES: Record<string, string> = {
    responsabilidadOrden: 'Responsabilidad y Orden',
    estabilidadEmocional: 'Estabilidad Emocional',
    energiaSocial: 'Energía Social',
    cooperacion: 'Cooperación',
    aperturaAdaptabilidad: 'Apertura y Adaptabilidad',
  };
  const miniIPIPOrder = ['responsabilidadOrden', 'estabilidadEmocional', 'energiaSocial', 'cooperacion', 'aperturaAdaptabilidad'];
  const miniIPIPSection =
    miniIPIPDims
      ? `
      <h2>Perfil Integral de Personalidad Profesional</h2>
      ${miniIPIPOrder
        .map(
          (key) => {
            const d = miniIPIPDims[key];
            if (!d) return '';
            return `<p style="margin: 0.5em 0;"><strong>${escapeHtml(MINI_IPIP_DIM_NAMES[key] ?? key)}</strong> — ${d.percent}% · ${escapeHtml(d.level)}</p>
      <p style="margin: 0 0 0.5em 1em; font-size: 0.95em; color: #555;">${escapeHtml(d.text)}</p>`;
          }
        )
        .join('')}
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">Referencia: ${escapeHtml(normLabel)} (n=${nNorm})</p>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">Perfil laboral breve basado en autoinforme. Interpretar junto con entrevista y requisitos del puesto.</p>
      <hr/>`
      : '';

  const ml8Agg = options.moduleAggregates.ML_8 as Record<string, unknown> | undefined;
  const ml8Labels = ml8Agg?.labels as Record<string, string> | undefined;
  const ml8Derived = ml8Agg?.derived as Record<string, { score: number; level: string; text: string }> | undefined;
  const levelLabel = (l: string) =>
    l === 'alto' ? 'Alto' : l === 'adecuado' ? 'Adecuado' : l === 'en_desarrollo' ? 'En desarrollo' : l;
  const ml8Section =
    ml8Labels
      ? `
      <h2>Motivadores Laborales</h2>
      <ul style="margin: 0.5em 0; padding-left: 1.5em;">
        <li><strong>Estabilidad ↔ Crecimiento:</strong> ${escapeHtml(ml8Labels.label_dim1 ?? ml8Labels.dim1Label ?? '—')}</li>
        <li><strong>Compensación ↔ Ambiente:</strong> ${escapeHtml(ml8Labels.label_dim2 ?? ml8Labels.dim2Label ?? '—')}</li>
        <li><strong>Autonomía ↔ Guía:</strong> ${escapeHtml(ml8Labels.label_dim3 ?? ml8Labels.dim3Label ?? '—')}</li>
        <li><strong>Rutina ↔ Variedad:</strong> ${escapeHtml(ml8Labels.label_dim4 ?? ml8Labels.dim4Label ?? '—')}</li>
      </ul>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">Este perfil describe preferencias de motivación laboral; no evalúa desempeño ni diagnóstico.</p>
      ${ml8Derived
        ? `
      <h3>Indicadores derivados</h3>
      ${ml8Derived.persistenciaPresion
        ? `<p style="margin: 0.5em 0;"><strong>Persistencia y Desempeño bajo Presión</strong> — <strong>${escapeHtml(levelLabel(ml8Derived.persistenciaPresion.level))}</strong>: ${escapeHtml(ml8Derived.persistenciaPresion.text)}</p>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(ml8DerivedNote)}</p>`
        : ''}
      ${ml8Derived.interaccionProfesional
        ? `<p style="margin: 0.5em 0;"><strong>Competencias de Interacción Profesional</strong> — <strong>${escapeHtml(levelLabel(ml8Derived.interaccionProfesional.level))}</strong>: ${escapeHtml(ml8Derived.interaccionProfesional.text)}</p>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(ml8DerivedNote)}</p>`
        : ''}
      `
        : ''}
      <hr/>`
      : '';

  const affinityInterp =
    options.jobProfile && Object.keys(options.jobProfile).length > 0
      ? computeAffinityInterpretation({
          jobProfile: options.jobProfile as Record<string, { testId: string; minScore?: number; weight?: number; dimensions?: Record<string, { expectedLevel: string; weight: number }> }>,
          scores: options.scores.map((s) => ({
            testId: s.testId,
            scorePercent: s.scorePercent ?? (typeof s.scaleScore === 'number' ? s.scaleScore : undefined),
            scaleScore: s.scaleScore,
          })),
          moduleAggregates: options.moduleAggregates,
          roleType: options.roleType,
        })
      : null;

  const affinitySection =
    affinityInterp
      ? `
      <h2>Interpretación de la afinidad al puesto</h2>
      <p style="margin: 0.5em 0;">La afinidad (${affinityInterp.affinityPercent}%) indica qué tan alineado está el candidato con el perfil ideal para <strong>${escapeHtml(affinityInterp.roleLabel)}</strong>.</p>
      <p style="margin: 0.5em 0;"><strong>Qué se valora más en este puesto:</strong></p>
      <table border="1" cellpadding="6" style="border-collapse: collapse; font-size: 0.9em;">
        <thead><tr><th>Dimensión</th><th>Importancia</th><th>Esperado</th><th>Candidato</th><th>Coincide</th></tr></thead>
        <tbody>
          ${affinityInterp.items
            .map(
              (i) =>
                `<tr><td>${escapeHtml(i.name)}</td><td>${escapeHtml(i.importance)}</td><td>${escapeHtml(i.expected)}</td><td>${escapeHtml(i.candidate)}</td><td>${getMatchLabel(i.match)}</td></tr>`
            )
            .join('')}
        </tbody>
      </table>
      <p style="margin: 0.5em 0;"><strong>Resumen:</strong> ${escapeHtml(affinityInterp.summaryText)}</p>
      <hr/>`
      : '';

  const culturalFitSection =
    options.culturalFit
      ? `
      <h2>Ajuste cultural organizacional</h2>
      <p style="margin: 0.5em 0;">
        Ajuste cultural estimado: <strong>${options.culturalFit.percent}%</strong>
      </p>
      <p style="margin: 0.5em 0;">
        Comparación por dimensión (base 100, % de dominancia normalizado):
      </p>
      <table border="1" cellpadding="6" style="border-collapse: collapse; font-size: 0.9em;">
        <thead><tr><th>Dimensión</th><th>Candidato</th><th>Empresa</th></tr></thead>
        <tbody>
          <tr><td>Clan</td><td>${options.culturalFit.candidateScores.clan}</td><td>${options.culturalFit.companyScores.clan}</td></tr>
          <tr><td>Adocracia</td><td>${options.culturalFit.candidateScores.adhocracy}</td><td>${options.culturalFit.companyScores.adhocracy}</td></tr>
          <tr><td>Mercado</td><td>${options.culturalFit.candidateScores.market}</td><td>${options.culturalFit.companyScores.market}</td></tr>
          <tr><td>Jerarquía</td><td>${options.culturalFit.candidateScores.hierarchy}</td><td>${options.culturalFit.companyScores.hierarchy}</td></tr>
        </tbody>
      </table>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">
        El ajuste se calcula con distancia euclidiana inversa (escala 0–100 por dimensión) entre el perfil del candidato y la cultura actual agregada de la empresa.
      </p>
      <hr/>`
      : '';

  const pcl10Score = options.scores.find((s) => s.testId === 'PCL_10');
  const pcl10Agg = options.moduleAggregates.PCL_10 as Record<string, unknown> | undefined;
  const totalPCL =
    pcl10Score != null
      ? (pcl10Score.rawScore ?? pcl10Score.scaleScore ?? 0)
      : (pcl10Agg?.totalScore as number | undefined) ?? 0;
  const confiabilidadSection =
    totalPCL >= 10
      ? (() => {
          const derived = computePCL10DerivedConfianza(totalPCL);
          return `
      <h2>Confiabilidad y Ética Profesional</h2>
      <p style="margin: 0.5em 0;"><strong>Indicador:</strong> ${derived.trustPercent}%</p>
      <p style="margin: 0.5em 0;"><strong>Nivel:</strong> ${escapeHtml(derived.level)}</p>
      <p style="margin: 0.5em 0;">${escapeHtml(derived.text)}</p>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(CONFIABILIDAD_ETICA_DERIVED_NOTE)}</p>
      <hr/>`;
        })()
      : '';

  const rfbScore = options.scores.find((s) => s.testId === 'RFB_SJ_8');
  const rfbTotalRaw =
    rfbScore != null
      ? (rfbScore.rawScore ?? rfbScore.scaleScore)
      : (rfbAgg?.totalScore as number | undefined);
  const hasRfb = rfbTotalRaw != null;
  const rfbTotal = hasRfb ? rfbTotalRaw! : 0;
  const criterioOperativoSection =
    hasRfb && totalPCL >= 10
      ? (() => {
          const derived = computeCriterioOperativoNormasDerived(rfbTotal, totalPCL);
          return `
      <h2>Criterio Operativo y Apego a Normas</h2>
      <p style="margin: 0.5em 0;"><strong>Indicador:</strong> ${derived.operationalNormsIndex}%</p>
      <p style="margin: 0.5em 0;"><strong>Nivel:</strong> ${escapeHtml(derived.level)}</p>
      <p style="margin: 0.5em 0;">${escapeHtml(derived.text)}</p>
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(CRITERIO_OPERATIVO_DERIVED_NOTE)}</p>
      <hr/>`;
        })()
      : '';

  const rfbFirstChoices = rfbAgg?.firstChoices as string[] | undefined;
  const persistedEstilo = rfbAgg?.derived as Record<string, { label: string; text: string; noteOptional?: string }> | undefined;
  const hasFirstChoices = rfbFirstChoices && rfbFirstChoices.length >= 8;
  const estiloDerived =
    persistedEstilo?.estiloDecisiones ??
    (hasFirstChoices ? computeEstiloDecisionesDerived(rfbFirstChoices!) : null);
  if (hasRfb && !estiloDerived && !hasFirstChoices && import.meta.env.DEV) {
    console.debug('[Estilo Decisiones] Datos insuficientes para derivado: firstChoices no persistidos.');
  }
  const estiloDecisionesSection =
    hasRfb && estiloDerived
      ? `
      <h2>Estilo de Toma de Decisiones Laborales</h2>
      <p style="margin: 0.5em 0;"><strong>${escapeHtml(estiloDerived.label)}</strong></p>
      <p style="margin: 0.5em 0;">${escapeHtml(estiloDerived.text)}</p>
      ${estiloDerived.noteOptional ? `<p style="margin: 0.5em 0; font-size: 0.95em; color: #8b6914;">${escapeHtml(estiloDerived.noteOptional)}</p>` : ''}
      <p style="margin: 0.5em 0; font-size: 0.95em; color: #555;">${escapeHtml(ESTILO_DECISIONES_DERIVED_NOTE)}</p>
      <hr/>`
      : '';

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const psyTalentLogo = `${origin}/isotipo.png`;
  const logoHeader =
    options.logoUrl
      ? `
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid #e5e7eb;">
        <img src="${escapeHtml(psyTalentLogo)}" alt="PsyTalent" width="48" height="48" style="object-fit: contain;" onerror="this.style.display='none'">
        <img src="${escapeHtml(options.logoUrl)}" alt="Logo empresa" style="max-width: 120px; max-height: 40px; object-fit: contain; margin-left: auto;" onerror="this.style.display='none'">
      </div>`
      : `
      <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem;">
        <img src="${escapeHtml(psyTalentLogo)}" alt="PsyTalent" width="48" height="48" style="object-fit: contain;" onerror="this.style.display='none'">
      </div>`;

  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head><meta charset="utf-8"><title>Reporte PsyTalent</title></head>
    <body style="font-family: sans-serif; padding: 2rem;">
      ${logoHeader}
      <h1>PsyTalent — Reporte de evaluación</h1>
      <p><strong>Candidato:</strong> ${escapeHtml(options.candidateName)}</p>
      <p><strong>Proceso:</strong> ${escapeHtml(options.processTitle) || '—'}</p>
      <p><strong>Empresa:</strong> ${escapeHtml(options.companyName)}</p>
      <p><strong>Fecha:</strong> ${reportDate} (Ciudad de México)</p>
      <hr/>
      <h2>Resumen por prueba (nombre comercial)</h2>
      <table border="1" cellpadding="8" style="border-collapse: collapse;">
        <thead><tr><th>Prueba</th><th>Puntuación</th><th>Rango</th><th>Resumen RH</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      ${culturalFitSection}
      ${affinitySection}
      ${pedSection}
      ${eel12Section}
      ${ra10Section}
      ${ra10DerivedSection}
      ${jkOaSection}
      ${jkSrSection}
      ${apo6Section}
      ${confiabilidadSection}
      ${criterioOperativoSection}
      ${estiloDecisionesSection}
      ${miniIPIPSection}
      ${ml8Section}
      <p style="font-size: 0.9em; color: #666;">Uso exclusivo para Recursos Humanos. No constituye diagnóstico. Anexo técnico con nombres de instrumentos disponible en versión extendida.</p>
      <p style="font-size: 0.85em; color: #999;">Generado por PsyTalent · ${generatedAt} (Ciudad de México)</p>
    </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
  win.onafterprint = () => win.close();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
